import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Users, Clock, Calendar, Activity } from 'lucide-react';

interface SocialEvent {
  time: string;
  type: 'IN_PERSON' | 'PHONE' | 'VIDEO' | 'GROUP';
  duration: number;
  participants: number;
  location: string;
  engagement: number;
}

interface SocialMetrics {
  dailyInteractions: number;
  totalDuration: number;
  averageEngagement: number;
  uniqueContacts: number;
  weeklyTrend: 'INCREASING' | 'STABLE' | 'DECREASING';
}

const SocialInteractions: React.FC = () => {
  // Mock data - in real app this would come from various sensors and devices
  const socialEvents: SocialEvent[] = [
    { time: '9:00 AM', type: 'IN_PERSON', duration: 45, participants: 2, location: 'Living Room', engagement: 85 },
    { time: '11:30 AM', type: 'PHONE', duration: 15, participants: 1, location: 'Kitchen', engagement: 75 },
    { time: '2:00 PM', type: 'VIDEO', duration: 30, participants: 3, location: 'Study', engagement: 90 },
    { time: '4:30 PM', type: 'GROUP', duration: 60, participants: 5, location: 'Community Room', engagement: 95 },
    { time: '7:00 PM', type: 'PHONE', duration: 20, participants: 1, location: 'Bedroom', engagement: 80 }
  ];

  const weeklyData = [
    { day: 'Mon', inPerson: 2, phone: 1, video: 1, total: 4 },
    { day: 'Tue', inPerson: 1, phone: 2, video: 0, total: 3 },
    { day: 'Wed', inPerson: 3, phone: 1, video: 1, total: 5 },
    { day: 'Thu', inPerson: 2, phone: 2, video: 1, total: 5 },
    { day: 'Fri', inPerson: 2, phone: 1, video: 2, total: 5 },
    { day: 'Sat', inPerson: 4, phone: 0, video: 1, total: 5 },
    { day: 'Sun', inPerson: 1, phone: 2, video: 0, total: 3 }
  ];

  const metrics: SocialMetrics = {
    dailyInteractions: 5,
    totalDuration: 170,
    averageEngagement: 85,
    uniqueContacts: 8,
    weeklyTrend: 'INCREASING'
  };

  const getEngagementColor = (engagement: number) => {
    if (engagement >= 85) return 'text-green-500';
    if (engagement >= 70) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'INCREASING':
        return 'text-green-500';
      case 'STABLE':
        return 'text-blue-500';
      case 'DECREASING':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getInteractionIcon = (type: string) => {
    switch (type) {
      case 'IN_PERSON':
        return <Users className="text-blue-500" />;
      case 'PHONE':
        return <Clock className="text-purple-500" />;
      case 'VIDEO':
        return <Activity className="text-green-500" />;
      case 'GROUP':
        return <Users className="text-orange-500" />;
      default:
        return <Users className="text-gray-500" />;
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Social Interaction Monitor</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Users className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Daily Interactions</h3>
          </div>
          <p className="text-3xl font-bold">{metrics.dailyInteractions}</p>
          <p className={`text-sm ${getTrendColor(metrics.weeklyTrend)}`}>
            Trend: {metrics.weeklyTrend.toLowerCase()}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Clock className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Social Duration</h3>
          </div>
          <p className="text-3xl font-bold">{metrics.totalDuration}min</p>
          <p className="text-sm text-gray-500">Total today</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Activity className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Engagement Level</h3>
          </div>
          <p className={`text-3xl font-bold ${getEngagementColor(metrics.averageEngagement)}`}>
            {metrics.averageEngagement}%
          </p>
          <p className="text-sm text-gray-500">Average today</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Users className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Unique Contacts</h3>
          </div>
          <p className="text-3xl font-bold">{metrics.uniqueContacts}</p>
          <p className="text-sm text-gray-500">This week</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Weekly Interaction Pattern</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={weeklyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="inPerson" name="In-Person" fill="#3b82f6" />
              <Bar dataKey="phone" name="Phone Calls" fill="#8b5cf6" />
              <Bar dataKey="video" name="Video Calls" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Today's Social Timeline</h3>
          <div className="space-y-4">
            {socialEvents.map((event, index) => (
              <div key={index} className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  {getInteractionIcon(event.type)}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{event.time}</span>
                    <span className={`text-sm ${getEngagementColor(event.engagement)}`}>
                      Engagement: {event.engagement}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {event.type.replace('_', ' ')} • {event.duration}min • {event.participants} participant(s)
                  </p>
                  <p className="text-sm text-gray-500">Location: {event.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Social Engagement Insights</h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Calendar className="text-blue-500" />
            </div>
            <div>
              <h4 className="font-medium">Weekly Pattern</h4>
              <p className="text-gray-600">Higher social engagement on weekends with group activities. Consider maintaining this pattern.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Activity className="text-green-500" />
            </div>
            <div>
              <h4 className="font-medium">Engagement Quality</h4>
              <p className="text-gray-600">Strong engagement levels during video calls suggest comfort with digital communication.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Users className="text-purple-500" />
            </div>
            <div>
              <h4 className="font-medium">Social Network</h4>
              <p className="text-gray-600">Good mix of individual and group interactions indicates healthy social network diversity.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialInteractions; 